import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Container, Image, Button } from 'react-bootstrap';
import LFlogo from './assets/leanfitt_pro.png';
import TermsPage from './components/public/TermsPage';
// Import components from their correct locations
import { 
  // Layouts
  AuthWrapper,
  
  // Screens
  Home,
  Reports,
  ActionItems,
  Projects,
  Profile,
  Awards,
  Tools,
  Quizzes,
  OrganizationManagement,
  ActionItemsByDate,
  
  // Public Components
  LandingPage,
  ReportPdf,
  ReportVsm as ReportVsmPdf,
  ReportChartPdf,
  ReportDataChartPdf,
  InviteAccept,
  
  // Shared Components
  DataCleanup,
  OrganizationSelector
} from './components';

// Direct imports for components not in index.js
import ReportChartView from './components/ReportChartView';
import TestPdf from './components/TestPdf';
import DeleteAccount from './components/DeleteAccount';
import TrialGuard from './components/auth/TrialGuard';
import AccountSetup from './components/AccountSetup';
import AccountSetupGate from './components/auth/AccountSetupGate';
import MaintenanceGate from './components/auth/MaintenanceGate';
import MaintenancePage from './components/screens/MaintenancePage';
import { DEFAULT_MAINTENANCE_CONFIG, fetchMaintenanceConfig } from './utils/maintenance';
import LearningEdit from './components/Learning/LearningEdit';
import LearningList from './components/Learning/LearningList';
import LearningView from './components/Learning/LearningView';
import LearningProgressAdmin from './components/Learning/LearningProgressAdmin';
import LearningTrackingDebug from './components/debug/LearningTrackingDebug';
import LearningAwardsDebug from './components/debug/LearningAwardsDebug';
import KPIView from './components/screens/KPIView';
import AddKpiPage from './components/KPI/AddKpiPage';
import Report5s from './components/reports/Report5s';
import QuizEdit from './components/Learning/QuizEdit';
import QuizList from './components/Learning/QuizList';
import QuizTake from './components/Learning/QuizTake';
import ProjectView from './components/screens/ProjectView';
import KPIViewPDF from './components/KPI/KPIView';
import SuperAdminConsole from './components/screens/SuperAdminConsole';
import SuperAdminRoute from './components/auth/SuperAdminRoute';
import AdministrativeFunctions from './components/screens/AdministrativeFunctions';
import QuickGuide from './components/screens/QuickGuide';
import IssueReporting from './components/screens/IssueReporting';
import IssueDetailPage from './components/screens/IssueDetailPage';
import TestFormattedHighlights from './components/TestFormattedHighlights';
import SimpleHighlightTest from './components/SimpleHighlightTest';
import ActionItemsList from './components/screens/ActionItemsList';
import UserShop from './components/shop/UserShop';
import UserInventory from './components/shop/UserInventory';
import ShopManagement from './components/shop/ShopManagement';
import { useOrganization } from './contexts/OrganizationContext';
import ReportBs from './components/reports/ReportBs';
import BoardView from './components/reports/BoardView';
import ReportHg from './components/reports/ReportHg';
import ReportSw from './components/reports/ReportSw';
import ReportVsm from './components/reports/ReportVsm';
import ProjectDetailsPdf from './components/public/ProjectDetailsPdf';
// Import the ChatBot component
import ChatBot from './components/ChatBot';
import OCPSetup from './components/screens/OCPSetup';
// Import the preview components
import SampleDataPreview from './components/preview/SampleDataPreview';
import SampleListPage from './components/preview/SampleListPage';

const LoginRoute = () => {
  const location = useLocation();
  const { activeOrganization } = useOrganization();
  const { authStatus, user } = useAuthenticator((context) => [context.authStatus, context.user]);

  // Sign-in hands off straight to the FITTWorks hosted UI (that page owns sign-in / create-account /
  // forgot-password, and returns to oauth.redirectSignIn = this app's origin). EXCEPT while maintenance
  // is on: then only /login?key=<loginBypassKey> may reach the hub; everyone else is shown the
  // maintenance page here, so they cannot sign in or migrate. (The whitelist gates app ACCESS after
  // sign-in; this gates whether sign-in can even start.)
  const [loopGuard, setLoopGuard] = useState(false);
  const [blockedConfig, setBlockedConfig] = useState(null);

  useEffect(() => {
    if (authStatus !== 'unauthenticated') return;
    let cancelled = false;
    (async () => {
      let config = { enabled: false };
      try { config = await fetchMaintenanceConfig(); } catch { /* fail open — never lock out on a glitch */ }
      if (cancelled) return;
      const key = new URLSearchParams(window.location.search).get('key');
      const bypassed = Boolean(config.loginBypassKey) && key === config.loginBypassKey;
      if (config.enabled && !bypassed) {
        setBlockedConfig(config); // show the maintenance page instead of redirecting to the hub
        return;
      }
      // Guard an infinite bounce with the hub: if we redirected moments ago, show the button instead.
      const last = Number(sessionStorage.getItem('hubRedirectAt') || 0);
      if (Date.now() - last < 15000) {
        setLoopGuard(true);
        return;
      }
      sessionStorage.setItem('hubRedirectAt', String(Date.now()));
      Auth.federatedSignIn();
    })();
    return () => { cancelled = true; };
  }, [authStatus]);

  const handleSignIn = () => {
    sessionStorage.setItem('hubRedirectAt', String(Date.now()));
    Auth.federatedSignIn();
  };

  if (authStatus === 'authenticated') {
    if (user && !activeOrganization) {
      return (
        <Container fluid className="p-0 min-vh-100 d-flex flex-column justify-content-center align-items-center" style={{ background: '#f8f9fa' }}>
          <div className="text-center">
            <h3 className="mb-4">Welcome to LeanFITT™ Pro</h3>
            <OrganizationSelector />
          </div>
        </Container>
      );
    }
    const from = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  // Maintenance is on and this visit has no valid bypass key → hold them here, don't reach the hub.
  if (blockedConfig) {
    return <MaintenancePage config={blockedConfig} />;
  }

  if (!loopGuard) {
    return (
      <Container fluid className="p-0 min-vh-100 d-flex flex-column justify-content-center align-items-center" style={{ background: '#f8f9fa' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="text-muted mb-0">Taking you to FITTWorks sign-in…</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0 min-vh-100 d-flex flex-column justify-content-center align-items-center" style={{ background: '#f8f9fa' }}>
      <div
        className="text-center"
        style={{
          background: 'white',
          padding: '2.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          maxWidth: '420px',
          width: '100%'
        }}
      >
        <Image
          src={LFlogo}
          alt="LeanFitt Logo"
          style={{
            maxWidth: '160px',
            marginBottom: '1.5rem'
          }}
        />
        <h2 className="mb-2" style={{ color: '#333' }}>Welcome to LeanFITT™ Pro</h2>
        <p className="text-muted mb-4">Sign in to access your dashboard and manage your organization</p>
        <Button
          onClick={handleSignIn}
          className="w-100"
          size="lg"
          style={{ background: '#007bff', border: 'none' }}
        >
          Sign In with FITTWorks
        </Button>
        <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.8rem' }}>
          Sign in, create an account or reset your password on the FITTWorks page.
        </p>
      </div>
    </Container>
  );
};

// Build-time maintenance gate. When REACT_APP_MAINTENANCE=1, block the login entry and the whole
// authenticated app, but leave the public marketing pages (/landing, /terms) and the headless PDF
// render routes reachable — so the site isn't dark and PDF generation keeps working. Toggling it
// requires a redeploy (CRA inlines env vars at build time).
const MAINTENANCE = process.env.REACT_APP_MAINTENANCE === '1';
const maintenanceElement = <MaintenancePage config={DEFAULT_MAINTENANCE_CONFIG} />;

// Root URL. Guests see the public landing page (no auto-bounce to the hub). Authenticated users are
// handed into the app via LoginRoute's authenticated branch (→ /dashboard, or the org selector when
// they have no active org) — so the hub's post-sign-in return to "/" lands logged-in users on the
// dashboard instead of stranding them on the marketing page.
const RootRoute = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  return authStatus === 'authenticated' ? <LoginRoute /> : <LandingPage />;
};

const AppRouter = () => {
  return (
    <Routes>
      {/* LeanFITT Pro landing page at root URL */}
      <Route path="/" element={MAINTENANCE ? maintenanceElement : <RootRoute />} />
      <Route path="/landing" element={<LandingPage />} />

      <Route path="/login" element={MAINTENANCE ? maintenanceElement : <LoginRoute />} />
      <Route path="/invite" element={<InviteAccept />} />
      <Route path="/account-setup" element={<AccountSetup />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
      
      {/* Public Routes - No Auth Required */}
      <Route path="/report_pdf/:reportId" element={<ReportPdf />} />
      <Route path="/report_vsm/:reportId" element={<ReportVsmPdf />} />
      <Route path="/report/Charts/:reportId" element={<ReportChartPdf />} />
      <Route path="/report/Charts_view/:reportId" element={<ReportChartView />} />
      <Route path="/report_Chart/:reportId" element={<ReportDataChartPdf />} />
      <Route path="/kpi_pdf/:kpiId" element={<KPIViewPDF />} />
      <Route path="/project_details/:projectId" element={<ProjectDetailsPdf />} />
      <Route path="/preview/samples" element={<SampleDataPreview />} />
      <Route path="/preview/list" element={<SampleListPage />} />
      
      <Route element={MAINTENANCE ? maintenanceElement : <MaintenanceGate><AccountSetupGate><TrialGuard><AuthWrapper /></TrialGuard></AccountSetupGate></MaintenanceGate>}>
        {/* Dashboard (authenticated home) */}
        <Route path="/dashboard" element={<Home />} />

        {/* Main */}
        <Route path="/reports" element={<Reports />} />
        <Route path="/action-items" element={<ActionItems />} />
        <Route path="/action-items-list" element={<ActionItemsList />} />
        <Route path="/action-items/:reportId?" element={<ActionItems />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/project/:projectId" element={<ProjectView />} />
        <Route path="/learnings" element={<LearningList />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/awards" element={<Awards />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/quizzes" element={<Quizzes />} />
        <Route path="/organization-management" element={<OrganizationManagement />} />
        {/* Distinct path so this is not shadowed by "/action-items/:reportId?" above.
            The :date param name is preserved, so ActionItemsByDate (useParams().date) is unchanged. */}
        <Route path="/action-items-by-date/:date" element={<ActionItemsByDate />} />
        <Route path="/kpi/:kpiId" element={<KPIView />} />
        <Route path="/project/:projectId/kpi/new" element={<AddKpiPage />} />
        <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminConsole /></SuperAdminRoute>} />
        <Route path="/start-smart" element={<AdministrativeFunctions />} />
        <Route path="/quick-guide" element={<QuickGuide />} />
        <Route path="/issue-reporting" element={<IssueReporting />} />
        <Route path="/issue/:issueId" element={<IssueDetailPage />} />
        <Route path="/chatbot" element={<ChatBot />} />
        <Route path="/ocp-setup" element={<OCPSetup />} />
        
        {/* Shop Routes */}
        <Route path="/shop" element={<UserShop />} />
        <Route path="/inventory" element={<UserInventory />} />
        <Route path="/organization-management/shop" element={<ShopManagement />} />
        
        {/* Reports */}
        <Route path="/report/:reportId" element={<Report5s />} />
        <Route path="/report/bs/:reportId" element={<ReportBs />} />
        <Route path="/report/board/:reportId" element={<BoardView />} />
        <Route path="/report/hg/:reportId" element={<ReportHg />} />
        <Route path="/report/sw/:reportId" element={<ReportSw />} />
        <Route path="/report/vsm/:reportId" element={<ReportVsm />} />
        
        {/* Others */}
        <Route path="/test" element={<TestPdf />} />
        <Route path="/test-formatted" element={<TestFormattedHighlights />} />
        <Route path="/test-simple" element={<SimpleHighlightTest />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        

        {/* Learnings */}
        <Route path="/learning/:learningId/edit" element={<LearningEdit />} />
        <Route path="/learning/:learningId/view" element={<LearningView />} />
        <Route path="/learning/:learningId/quizzes" element={<QuizList />} />
        <Route path="/learning/:learningId/quiz/new" element={<QuizEdit />} />
        <Route path="/learning/:learningId/quiz/:quizId" element={<QuizEdit />} />
        <Route path="/learning/:learningId/quiz/:quizId/take" element={<QuizTake />} />
        
        {/* Learning Progress Admin */}
        <Route path="/learning-analytics" element={<LearningProgressAdmin />} />
        <Route path="/learning-debug" element={<LearningTrackingDebug />} />
        <Route path="/learning-awards-debug" element={<LearningAwardsDebug />} />
      </Route>

      {/* Catch all route - redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRouter; 